import { useEffect, useState } from "react";
import {
  searchRepertoireLibrary,
  type RepertoireLibraryPiece,
} from "./repertoire.js";

export interface RepertoireSearchPickerProps {
  onSelect: (piece: RepertoireLibraryPiece) => void;
  excludeIds?: readonly string[];
  label?: string;
  placeholder?: string;
  emptyMessage?: string;
  maxResults?: number;
  autoFocus?: boolean;
  disabled?: boolean;
}

/**
 * The one repertoire search control used by teaching workflows.
 *
 * It searches recent items before anything is typed, then title, artist /
 * composer, collection, instrument, genre and tags through the server as the
 * tutor types. Keeping this in one component prevents Groove Builder, student
 * repertoire and future teaching tools from each inventing a different picker.
 */
export function RepertoireSearchPicker({
  onSelect,
  excludeIds = [],
  label = "Search repertoire",
  placeholder = "Song, artist, composer, instrument, genre…",
  emptyMessage = "No repertoire matches that search.",
  maxResults = 8,
  autoFocus = false,
  disabled = false,
}: RepertoireSearchPickerProps) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<RepertoireLibraryPiece[]>([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);
  const excluded = new Set(excludeIds);

  useEffect(() => {
    let live = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setProblem(null);
      void searchRepertoireLibrary(query)
        .then((next) => {
          if (live) setItems(next);
        })
        .catch((cause: unknown) => {
          if (live) {
            setProblem(
              cause instanceof Error
                ? cause.message
                : "Could not search the repertoire library.",
            );
          }
        })
        .finally(() => {
          if (live) setLoading(false);
        });
    }, 180);

    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  const visible = items.filter((piece) => !excluded.has(piece.id)).slice(0, maxResults);

  return (
    <div className="stack" style={{ gap: 8 }}>
      <label>
        <strong>{label}</strong>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          autoFocus={autoFocus}
          disabled={disabled}
        />
      </label>

      <div className="small muted">
        {loading
          ? "Searching…"
          : query.trim()
            ? `${visible.length} ${visible.length === 1 ? "match" : "matches"}`
            : "Recently updated repertoire"}
      </div>

      {problem && <div className="error small">{problem}</div>}

      {!loading && !problem && visible.length === 0 && (
        <div className="small muted">{emptyMessage}</div>
      )}

      {visible.length > 0 && (
        <div className="stack" style={{ gap: 6 }}>
          {visible.map((piece) => (
            <button
              type="button"
              className={`quiet repertoire-picker-result${query.trim() ? "" : " is-recent"}`}
              key={piece.id}
              disabled={disabled}
              style={{ textAlign: "left", width: "100%" }}
              onClick={() => {
                onSelect(piece);
                setQuery("");
              }}
            >
              <strong className="repertoire-picker-result-title">{piece.title}</strong>
              <span className="small muted repertoire-picker-result-meta" style={{ marginTop: 2 }}>
                <span className="repertoire-picker-result-meta-mobile">
                  {piece.composer || "Repertoire item"}
                </span>
                <span className="repertoire-picker-result-meta-full">
                  {[piece.composer, piece.instrument, piece.genre].filter(Boolean).join(" · ") ||
                    "Repertoire item"}
                  {piece.assetCount !== undefined
                    ? ` · ${piece.assetCount} ${piece.assetCount === 1 ? "material" : "materials"}`
                    : ""}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
